import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, ChevronLeft, Image as ImageIcon, Lock, Ruler, X } from 'lucide-react';
import { portalOrder, portalSetStatus, portalSetStage, uploadFile } from '../../api/resources';
import { assetUrl } from '../../utils/format';
import { inr, fmtDate } from '../../utils/format';
import { useToast } from '../../context/ToastContext';
import Spinner from '../../components/ui/Spinner';
import MeasurementValue from '../../components/ui/MeasurementValue';
import { StatusBadge } from '../../components/ui/Badge';
import { WORK_LABEL, ZONE_LABEL, ZONE_ORDER, isPastDay } from './shared';

/**
 * Finishing a job means showing the work: at least one photo, which the
 * supervisor sees when they inspect the garment. The camera opens straight
 * away on a phone.
 */
function FinishJob({ job, busy, onClose, onFinish }) {
  const { error } = useToast();
  const [shots, setShots] = useState([]);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);

  const add = async (files) => {
    const list = [...files].slice(0, 6 - shots.length);
    if (!list.length) return;
    setUploading(true);
    try {
      const done = [];
      for (const f of list) {
        const res = await uploadFile(f);
        done.push(res.url);
      }
      setShots((s) => [...s, ...done]);
    } catch (e) {
      error(e.response?.data?.message || 'Could not upload that photo.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="card w-full max-w-md rounded-b-none p-4 sm:rounded-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-content">Finish this job</h3>
        <p className="mt-0.5 text-xs text-content-muted">
          {WORK_LABEL[job.workType]} · add a photo of your work if you can — your supervisor sees it when they check the garment.
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          {shots.map((src) => (
            <span key={src} className="relative">
              <img src={assetUrl(src)} alt="" className="h-20 w-20 rounded-lg border border-line object-cover" />
              <button
                type="button"
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-danger-600 text-white"
                onClick={() => setShots((s) => s.filter((x) => x !== src))}
                title="Remove"
              >
                <X size={12} />
              </button>
            </span>
          ))}
          {shots.length < 6 && (
            <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-brand-300 text-brand-700">
              <Camera size={20} />
              <span className="text-2xs font-bold uppercase">{uploading ? 'Wait…' : 'Photo'}</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={(e) => { add(e.target.files); e.target.value = ''; }}
              />
            </label>
          )}
        </div>

        <input
          className="input mt-3"
          placeholder="Anything to tell the supervisor? (optional)"
          value={note}
          maxLength={190}
          onChange={(e) => setNote(e.target.value)}
        />

        <div className="mt-4 flex gap-2">
          <button className="btn-outline flex-1" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="btn-primary flex-1"
            disabled={busy || uploading}
            onClick={() => onFinish({ proofImages: shots, proofNote: note.trim() || undefined })}
          >
            {busy ? 'Saving…' : shots.length ? 'Mark as Complete' : 'Complete without a photo'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Measurements({ zones }) {
  const filled = ZONE_ORDER.filter((z) => zones[z]?.length);
  if (!filled.length) {
    return <p className="rounded-lg bg-surface-sunken py-4 text-center text-xs text-content-subtle">No measurements were recorded for this item.</p>;
  }
  return (
    <div className={`grid gap-3 ${filled.length > 1 ? 'sm:grid-cols-2' : ''} ${filled.length > 2 ? 'lg:grid-cols-3' : ''}`}>
      {filled.map((z) => (
        <div key={z} className="rounded-lg border border-line">
          <p className="border-b border-line bg-surface-sunken px-3 py-1.5 text-2xs font-bold uppercase tracking-wider text-content-subtle">{ZONE_LABEL[z]}</p>
          <dl className="divide-y divide-line">
            {zones[z].map((m) => (
              <div key={m.measurementId} className="flex items-baseline justify-between gap-3 px-3 py-1.5">
                <dt className="text-sm text-content-muted">
                  {m.name}
                  {m.notes && <span className="block text-2xs text-content-subtle">{m.notes}</span>}
                </dt>
                <dd className="text-base font-semibold text-content">
                  <MeasurementValue value={m.value} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}

export default function PortalOrder() {
  const { invoiceId } = useParams();
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [confirm, setConfirm] = useState(null); // job to mark completed

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['portal-order', invoiceId],
    queryFn: () => portalOrder(invoiceId),
  });

  // Handing the garment on for checking. Only offered where the server allows
  // it (stitched or altered), so the button cannot promise what it cannot do.
  const stageMut = useMutation({
    mutationFn: ({ itemId, stage }) => portalSetStage(itemId, stage),
    onSuccess: (d) => { success(d.message || 'Sent on.'); refetch(); },
    onError: (e) => error(e.response?.data?.message || 'Could not send it on.'),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status, proof }) => portalSetStatus(id, status, proof),
    onSuccess: (d) => {
      success(d.message);
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ['portal-order', invoiceId] });
      qc.invalidateQueries({ queryKey: ['portal-orders'] });
      qc.invalidateQueries({ queryKey: ['portal-me'] });
    },
    onError: (e) => {
      error(e.response?.data?.message || 'Could not update the job.');
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ['portal-order', invoiceId] });
    },
  });

  if (isLoading) return <Spinner label="Loading order…" />;
  if (isError || !data?.order) {
    return (
      <div className="card p-8 text-center">
        <p className="text-sm text-content-muted">This order is not available to you.</p>
        <Link to="/portal/orders" className="mt-3 inline-block text-sm font-semibold text-brand-700 hover:underline">Back to my orders</Link>
      </div>
    );
  }

  const o = data.order;
  const late = o.myStatus !== 'COMPLETED' && isPastDay(o.deliveryDate);

  return (
    <div className="space-y-4">
      <Link to="/portal/orders" className="inline-flex items-center gap-1 text-sm font-medium text-content-muted hover:text-content">
        <ChevronLeft size={15} /> My Orders
      </Link>

      <section className="card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-2xs font-bold uppercase tracking-wider text-content-subtle">Order</p>
            <h2 className="font-display text-2xl font-semibold text-brand-900">{o.orderNo}</h2>
            <p className="text-sm text-content-muted">{o.customerName || '—'}</p>
          </div>
          <StatusBadge status={o.myStatus} />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div><p className="text-xs text-content-subtle">Order date</p>{fmtDate(o.orderDate) || '—'}</div>
          <div><p className="text-xs text-content-subtle">Trial</p>{o.trialDate ? fmtDate(o.trialDate) : '—'}</div>
          <div>
            <p className="text-xs text-content-subtle">Delivery</p>
            <span className={late ? 'font-semibold text-danger-600' : ''}>{o.deliveryDate ? fmtDate(o.deliveryDate) : '—'}</span>
          </div>
          <div><p className="text-xs text-content-subtle">My earnings</p><b className="tabular-nums">{inr(o.myEarnings)}</b></div>
        </div>
      </section>

      {o.items.map((it) => (
        <section key={it.invoiceItemId} className="card p-5">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-display text-lg font-semibold text-brand-900">
              {it.itemName} <span className="text-base font-normal text-content-muted">× {it.qty}</span>
            </h3>
            {it.forMember && <span className="text-xs text-content-subtle">for {it.forMember}</span>}
          </div>

          {/* Where this garment is, and what this worker may do next with it. */}
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-surface-sunken px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-content-subtle">Stage</span>
            <span className="rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-brand-700">
              {it.stageLabel || 'Order received'}
            </span>
            {it.holder && <span className="text-xs text-content-subtle">with {it.holder}</span>}
            {(it.stageMoves || []).map((m) => (
              <button
                key={m.stage}
                className="btn-primary btn-sm ml-auto"
                disabled={stageMut.isPending}
                onClick={() => stageMut.mutate({ itemId: it.invoiceItemId, stage: m.stage })}
              >
                Send for {m.label}
              </button>
            ))}
            {!(it.stageMoves || []).length && (
              <span className="ml-auto text-2xs text-content-subtle">
                Finish your job below and it moves on by itself
              </span>
            )}
          </div>

          <div className="mb-4 space-y-2">
            {it.jobs.map((j) => (
              <div key={j.assignmentId} className="flex flex-wrap items-center gap-3 rounded-lg border border-line px-3 py-2">
                <span className="w-24 text-sm font-semibold text-content">{WORK_LABEL[j.workType]}</span>
                <StatusBadge status={j.status} />
                <span className="text-sm tabular-nums text-content-muted">{inr(j.cost)}</span>
                {j.completedAt && <span className="text-xs text-content-subtle">done {fmtDate(j.completedAt)}</span>}
                {(j.proofImages || []).map((src) => (
                  <a key={src} href={assetUrl(src)} target="_blank" rel="noreferrer" title={j.proofNote || 'Photo you sent'}>
                    <img src={assetUrl(src)} alt="" className="h-9 w-9 rounded border border-line object-cover" />
                  </a>
                ))}
                <span className="ml-auto flex items-center gap-2">
                  {j.settled && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700"><Lock size={12} /> Paid</span>
                  )}
                  {j.waitingForCutting && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-warning-50 px-2 py-0.5 text-xs font-semibold text-warning-700">
                      Waiting for cutting
                    </span>
                  )}
                  {!j.settled && j.status === 'ASSIGNED' && (
                    <button
                      className="btn-outline btn-sm"
                      disabled={statusMut.isPending || j.waitingForCutting}
                      title={j.waitingForCutting ? j.blockedReason : undefined}
                      onClick={() => statusMut.mutate({ id: j.assignmentId, status: 'IN_PROGRESS' })}
                    >
                      Start work
                    </button>
                  )}
                  {!j.settled && j.status !== 'COMPLETED' && (
                    <button
                      className="btn-primary btn-sm"
                      disabled={statusMut.isPending || j.waitingForCutting}
                      title={j.waitingForCutting ? j.blockedReason : undefined}
                      onClick={() => setConfirm(j)}
                    >
                      Mark as Complete
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>

          {[...(it.images || []), ...(it.patterns || []), ...(it.handMarks || [])].length > 0 && (
            <div className="mb-4">
              <p className="mb-1.5 flex items-center gap-1.5 text-2xs font-bold uppercase tracking-wider text-content-subtle">
                <ImageIcon size={12} /> Pictures from the order
              </p>
              <div className="flex flex-wrap gap-2">
                {[...(it.images || []), ...(it.patterns || []), ...(it.handMarks || [])].map((src) => (
                  <a key={src} href={assetUrl(src)} target="_blank" rel="noreferrer" title="Open full size">
                    <img src={assetUrl(src)} alt="" className="h-24 w-24 rounded-lg border border-line object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {(it.styleSelections?.length > 0 || it.remarks) && (
            <div className="mb-4 flex flex-wrap items-center gap-1.5 text-xs">
              {it.styleSelections.map((s) => (
                <span key={s.styleTypeId || s.name} className="badge-neutral">{s.name}: {s.option}</span>
              ))}
              {it.remarks && <span className="text-content-muted">Note: {it.remarks}</span>}
            </div>
          )}

          <p className="mb-2 flex items-center gap-1.5 text-2xs font-bold uppercase tracking-wider text-content-subtle">
            <Ruler size={12} /> Measurements
          </p>
          <Measurements zones={it.measurements} />
        </section>
      ))}

      {confirm && (
        <FinishJob
          job={confirm}
          busy={statusMut.isPending}
          onClose={() => setConfirm(null)}
          onFinish={(proof) => statusMut.mutate({ id: confirm.assignmentId, status: 'COMPLETED', proof })}
        />
      )}
    </div>
  );
}
